import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChevronDownIcon } from '@pleio/icons/chevron-down'
import { FlagDeIcon } from '@pleio/icons/flag-de'
import { FlagEnIcon } from '@pleio/icons/flag-en'
import { FlagFrIcon } from '@pleio/icons/flag-fr'
import { FlagNlIcon } from '@pleio/icons/flag-nl'

import HideVisually from 'js/components/HideVisually/HideVisually'
import Popover from 'js/components/Popover'
import cx from 'js/lib/helpers/cx'

import styles from './LanguagePicker.module.css'
import type { Language, LanguagePickerViewProps } from './LanguagePicker.types'

const languages = [
    {
        value: 'de',
        Icon: FlagDeIcon,
        labelKey: 'editor.text-language-german',
        abbreviationKey: 'language-picker.abbreviation-de',
    },
    {
        value: 'en',
        Icon: FlagEnIcon,
        labelKey: 'editor.text-language-english',
        abbreviationKey: 'language-picker.abbreviation-en',
    },
    {
        value: 'fr',
        Icon: FlagFrIcon,
        labelKey: 'editor.text-language-french',
        abbreviationKey: 'language-picker.abbreviation-fr',
    },
    {
        value: 'nl',
        Icon: FlagNlIcon,
        labelKey: 'editor.text-language-dutch',
        abbreviationKey: 'language-picker.abbreviation-nl',
    },
] as const

const LanguagePickerView = ({
    value,
    options = languages.map((language) => language.value),
    onChange,
    className,
}: LanguagePickerViewProps) => {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)

    const selected =
        languages.find((language) => language.value === value) ?? languages[0]
    const otherLanguages = languages.filter(
        (language) =>
            language.value !== selected.value &&
            options.includes(language.value),
    )
    const SelectedIcon = selected.Icon

    const handleSelect = (language: Language) => {
        setOpen(false)
        onChange?.(language)
    }

    return (
        <Popover
            open={open}
            setOpen={setOpen}
            side="bottom"
            align="start"
            sideOffset={0}
            hideArrow
            animation="fade"
            ariaLabel={t('language-picker.choose-language')}
            content={
                <ul className={styles.list}>
                    {otherLanguages.map(({ value, Icon, labelKey }) => (
                        <li key={value}>
                            <button
                                type="button"
                                className={styles.option}
                                lang={value}
                                onClick={() => handleSelect(value)}
                            >
                                <Icon className={styles.flag} />
                                {t(labelKey, { lng: value })}
                            </button>
                        </li>
                    ))}
                </ul>
            }
        >
            <button type="button" className={cx(styles.trigger, className)}>
                <SelectedIcon className={styles.flag} />
                {t(selected.abbreviationKey)}{' '}
                <HideVisually>
                    {t('language-picker.change-language', {
                        language: t(selected.labelKey),
                    })}
                </HideVisually>
                <ChevronDownIcon className={styles.chevron} />
            </button>
        </Popover>
    )
}

export default LanguagePickerView
